import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { IResourceListItem, ResourceKindEnum } from './Models';
import { Dialog, DialogActions, DialogContent, Button, Paper, Tabs, Tab, Icon, AppBar, Checkbox } from '@material-ui/core';
import Filtering from './FilterManager/Filtering';
import { makeid } from '../../../common/utils/randomHelper';
import { Checkboxs } from '../../../common/controls/GridHelper/GridRowCheckboxs';
import { GridTabPanel } from '../../../common/controls/GridHelper/GridTabPanel';
import DialogTitleWithClose from '../../../common/components/DialogTitleWithClose';
import SearchDialogCheckbox from './SearchDialogCheckbox';

type SupportedTabKey = "GetServiceResources" | "GetRelatedResources" | "GetRelatedEmployesResources" | "GetResources";

interface IProps {
	kind: string,
	showGridTabs: string,
	filters: string;
	filterId: string,
	getEmployeesValue: () => string,
	getNotifierId: () => string,

	resourcesGridColumns: any,
	relatedResourcesGridColumns: any,
	relatedEmployeesResourcesGridColumns: any,
	servicesGridColumns: any,

	onSave: (r: IResourceListItem[]) => void,
	className: string,
	disabled: boolean,
	manageFiltersPermission: boolean,
}

export default function SearchButton(props: IProps) {
	const [open, setOpen] = React.useState(false);
	const { t } = useTranslation();

	return <>
		{(props.showGridTabs && props.showGridTabs !== 'hidden') && <Button
			disabled={props.disabled}
			className={props.className}
			variant="outlined"
			onClick={() => setOpen(true)}
			startIcon={<Icon>add_circle</Icon>}
		>
			{t("common.action.select")}
		</Button>}
		{open && <SearchDialog {...props} onClose={() => {
			setOpen(false)
		}} />}
	</>
}

interface ISearchDialogProps {
	kind: string,
	showGridTabs: string,
	filterId: string,
	filters: string;
	getEmployeesValue: () => string,
	getNotifierId: () => string,

	resourcesGridColumns: any,
	relatedResourcesGridColumns: any,
	relatedEmployeesResourcesGridColumns: any,
	servicesGridColumns: any,

	onSave: (r: IResourceListItem[]) => void,
	onClose: () => void,
	manageFiltersPermission: boolean,
}


function SearchDialog(props: ISearchDialogProps) {
	const { t } = useTranslation();
	const [currentTabIndex, setCurrentTabIndex] = React.useState(0);

	const [gridTempId, setGridIdValue] = React.useState("")

	React.useEffect(() => {
		setGridIdValue(makeid(5));
	}, [open])

	if (!props.showGridTabs) {
		console.warn("Required property \'showGridTabs\'.");
	}

	let tabs = [];
	if (props.showGridTabs) {
		tabs = props.showGridTabs.split(',')
			.map((tabKey: SupportedTabKey) => {

				let label = "";
				switch (tabKey) {
					case "GetServiceResources":
						label = t("control.resources.serviceResources");
						break;
					case "GetRelatedResources":
						label = t("control.resources.relatedResources");
						break;
					case "GetRelatedEmployesResources":
						label = t("control.resources.resourcesInRelationsWithEmployees");
						break;
					case "GetResources":
						label = t("control.resources.allResources");
						break;
				}

				return {
					key: tabKey,
					label: label
				}
			});
	}

	const currentTab: SupportedTabKey = tabs[currentTabIndex]?.key;

	const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
		setCurrentTabIndex(newValue);
	};

	const handleSave = () => {
		function getPropertyValue(model, data, property: keyof IResourceListItem): any {
			if (model[property]) {
				return data[model[property].index];
			}

			return null;
		}

		function getFocusedData(grid): IResourceListItem[] {
			var rows = (window as any).fnGetSelected(grid);
			if (rows == null || rows.length === 0) return null;
			var aReturn = [];
			for (var i = 0; i < rows.length; i++) {
				var data = grid.fnGetData(rows[i]);
				var model = grid.fnGetModelData(rows[i]);

				let kindString: string = data[model.KindEnum.index];

				var item: IResourceListItem = {
					Id: data[model.Id.index],
					Name: data[model.Name.index],
					Kind: data[model.Kind.index],
					KindEnum: ResourceKindEnum[kindString],
					Type: data[model.Type.index],

					STNumber: getPropertyValue(model, data, "STNumber"),
					SerialNumber: getPropertyValue(model, data, "SerialNumber"),
					AcceptantId: getPropertyValue(model, data, "AcceptantId"),
					ApplicationName: getPropertyValue(model, data, "ApplicationName"),
					BarCode: getPropertyValue(model, data, "BarCode"),
					Category: getPropertyValue(model, data, "Category"),
					Company: getPropertyValue(model, data, "Company"),
					Department: getPropertyValue(model, data, "Department"),
					EmployeeCompany: getPropertyValue(model, data, "EmployeeCompany"),
					EmployeeId: getPropertyValue(model, data, "EmployeeId"),
					EmployeeName: getPropertyValue(model, data, "EmployeeName"),
					GivenName: getPropertyValue(model, data, "GivenName"),
					HRId: getPropertyValue(model, data, "HRId"),
					Manufacturer: getPropertyValue(model, data, "Manufacturer"),
					Model: getPropertyValue(model, data, "Model"),
					MpkNumber: getPropertyValue(model, data, "MpkNumber"),
					ReferenceNumber: getPropertyValue(model, data, "ReferenceNumber"),
					Relation: getPropertyValue(model, data, "Relation"),
					RelationId: getPropertyValue(model, data, "RelationId"),
					ReplacementDate: getPropertyValue(model, data, "ReplacementDate"),
					SAPId: getPropertyValue(model, data, "SAPId"),
					Selected: getPropertyValue(model, data, "Selected"),
					Status: getPropertyValue(model, data, "Status"),
					Surname: getPropertyValue(model, data, "Surname"),
					UsageType: getPropertyValue(model, data, "UsageType"),
					IssueId: getPropertyValue(model, data, "IssueId"),
					Email: getPropertyValue(model, data, "Email"),
				};

				aReturn.push(item);
			}
			return aReturn;
		}

		function getRowsFromFocusedGrid() {

			switch (currentTab) {
				case "GetRelatedEmployesResources":
					let grid1 = (window as any).$('#' + gridTempId + "-grid1").dataTable({ bRetrieve: true });
					return getFocusedData(grid1);
				case "GetRelatedResources":
					let grid2 = (window as any).$('#' + gridTempId + "-grid2").dataTable({ bRetrieve: true });
					return getFocusedData(grid2);
				case "GetResources":
					let grid3 = (window as any).$('#' + gridTempId + "-grid3").dataTable({ bRetrieve: true });
					return getFocusedData(grid3);
				case "GetServiceResources":
					let grid4 = (window as any).$('#' + gridTempId + "-grid4").dataTable({ bRetrieve: true });
					return getFocusedData(grid4);
			}
			
			return null;
		}

		var rows = getRowsFromFocusedGrid();

		props.onClose();
		props.onSave(rows);
	}

	function createUrl(kind: string): string {
		let url = `/Resources/GetResources/?kind=${kind}`;
		if (props.filterId) {
			url += "&filterId=" + props.filterId;
		}
		if (props.filters) {
			url += "&filters=" + props.filters;
		}
		return url;
	}

	return (<Dialog
		open={true}
		onClose={props.onClose}
		fullWidth
		maxWidth="lg"
	>
		<DialogTitleWithClose onClose={props.onClose}>
			{t("common.action.select")}: {tabs[currentTabIndex]?.label}
		</DialogTitleWithClose>
		<DialogContent>
			<Paper square>
				{tabs.length > 1 &&
					<AppBar position="static" color="default">
						<Tabs
							value={currentTabIndex}
							indicatorColor="primary"
							textColor="primary"
							onChange={handleChange}
						>
							{tabs.map(x => <Tab key={x.key} label={t(x.label)} />)}
					</Tabs>
				</AppBar>}

				<GridTabPanel value={currentTab} index={"GetRelatedEmployesResources"}>
					<RelatedEmployesResourcesGrid
						{...props}
						gridId={gridTempId + "-grid1"}
					/>
				</GridTabPanel>
				<GridTabPanel value={currentTab} index={"GetRelatedResources"}>
					<RelatedResourcesUserGrid
						{...props}
						gridId={gridTempId + "-grid2"}
					/>
				</GridTabPanel>
				<GridTabPanel value={currentTab} index={"GetResources"}>
					<ShowResourcesGrid
						gridId={gridTempId + "-grid3"}
						kind={props.kind}
						ajaxSource={createUrl(props.kind)}
						gridColumns={props.resourcesGridColumns}
						manageFiltersPermission={props.manageFiltersPermission}
					/>
				</GridTabPanel>
				<GridTabPanel value={currentTab} index={"GetServiceResources"}>
					<ShowResourcesGrid
						gridId={gridTempId + "-grid4"}
						kind={props.kind}
						ajaxSource={createUrl("ServiceAsset")}
						gridColumns={props.servicesGridColumns}
						manageFiltersPermission={props.manageFiltersPermission}
					/>
				</GridTabPanel>
			</Paper>
		</DialogContent>
		<DialogActions>
			<Button onClick={props.onClose} variant="contained" color="default">
				{t("common.action.cancel")}
			</Button>
			<Button onClick={handleSave} color="primary" variant="contained" autoFocus>
				{t("common.action.ok")}
			</Button>
		</DialogActions>
	</Dialog>)
}

interface IRelatedEmployesResourcesGridProps {
	filterId: string;
	filters: string;
	gridId: string;
	kind: string;
	getEmployeesValue: () => string;
	relatedEmployeesResourcesGridColumns: any;
	manageFiltersPermission: boolean;
}

function RelatedEmployesResourcesGrid(props: IRelatedEmployesResourcesGridProps)
{
	if (!props.getEmployeesValue) {
		console.warn('Undefinied getEmployeesValue');
		return null;
	};

	let url = `/Resources/GetRelatedEmployesResources/?kind=${props.kind}`;
	if (props.filterId) {
		url += "&filterId=" + props.filterId;
	}
	if (props.filters) {
		url += "&filters=" + props.filters;
	}
	const employeesValue = props.getEmployeesValue();
	if (employeesValue) {
		url += "&employees=" + encodeURIComponent(props.getEmployeesValue());
	}

	return <ShowResourcesGrid
		gridId={props.gridId}
		kind={props.kind}
		ajaxSource={url}
		gridColumns={props.relatedEmployeesResourcesGridColumns}
		manageFiltersPermission={props.manageFiltersPermission}
	/>
}

interface IRelatedResourcesUserGridProps {
	filterId: string;
	filters: string;
	gridId: string;
	kind: string;
	getNotifierId: () => string;
	relatedResourcesGridColumns: any;
	manageFiltersPermission: boolean;
}

function RelatedResourcesUserGrid(props: IRelatedResourcesUserGridProps) {
	if (!props.getNotifierId) {
		console.warn('Undefinied getNotifierId');
		return null;
	};

	let url = `/Resources/GetRelatedResources/?kind=${props.kind}&userId=${encodeURIComponent(props.getNotifierId())}`;
	if (props.filterId) {
		url += "&filterId=" + props.filterId;
	}
	if (props.filters) {
		url += "&filters=" + props.filters;
	}

	return <ShowResourcesGrid
		gridId={props.gridId}
		kind={props.kind}
		ajaxSource={url}
		gridColumns={props.relatedResourcesGridColumns}
		manageFiltersPermission={props.manageFiltersPermission}
	/>
}

interface IShowResourcesGridProps {
	gridId: string,
	kind: string,
	ajaxSource: string,
	gridColumns: any,
	manageFiltersPermission: boolean,
}

function getLabel(value: string, key: string): string {
	const qdesk = (window as any).qdesk;
	if(!qdesk.enums)
		return value;

	const array = qdesk.enums[key];
	if(!array)
		return value;
	
	for (let i = 0; i < array.length; i++) {
		const item = array[i];
		if (item.value == value) 
			return item.label;
	}
}

function ShowResourcesGrid(props: IShowResourcesGridProps) {
	const [selected, setSelected] = React.useState<{ [key: string]: boolean }>({});
	const [addedNodes, setAddedNodes] = React.useState<HTMLElement[]>([]);
	const gridRef = React.useRef<HTMLDivElement>();
	const _filtering = React.useRef<Filtering>();
	const [pageCompletedGrid, setPageCompletedGrid] = React.useState<{ oGrid: any }>(null);
	const [handleRowSelect, setHandleRowSelect] = React.useState<{ nRow?: HTMLElement, selected?: boolean }>({});
	const forceHandleRowSelect = React.useCallback((nRow: HTMLElement, selected: boolean) => setHandleRowSelect({ nRow: nRow, selected: selected }), []);
	const forceUpdate = React.useCallback((gr) => setPageCompletedGrid({ oGrid: gr }), []);

	React.useEffect(() => {
		if (pageCompletedGrid) {
			renderCheckboxToColumn(pageCompletedGrid);
		}
	}, [pageCompletedGrid]);

	React.useEffect(() => {
		if (handleRowSelect.nRow) {
			let key = handleRowSelect.nRow.dataset.key;
			let copy = { ...selected };
			copy[key] = handleRowSelect.selected;
			setSelected(copy);
		};
	}, [handleRowSelect]);


	React.useEffect(() => {

		var url = props.ajaxSource;

		try {
			let gridSetup = new (window as any).GridSetup()
				.forId('#' + props.gridId)
				.setColumns(props.gridColumns)
				.withColumnSelector()
				.withFilterManager('ResourceListItem', _filtering.current.getManager())
				.setAjaxSource(url);

			const grid = gridSetup
				.onRowCallback(function (nRow, aData, iDisplayIndex) {

					let data = grid.fnGetModelData(aData);
					if (data.Company && data.Company.value) {
						let targetCompany = (window as any).$('td:eq(' + data.Company.visibleIndex + ')', nRow);
						(window as any).qdesk.controls.initLogCompanyTooltip(targetCompany[0], data.Company.value);
					}
					if (data.KindEnum && data.KindEnum.value) {
						let targetKindEnum = (window as any).$('td:eq(' + data.KindEnum.visibleIndex + ')', nRow);
						targetKindEnum.html(getLabel(data.KindEnum.value, "KindEnum"));
					}
				})
				.onRowSelect(function (e, oGrid, nRow) {
					let selected = (window as any).switchRow(nRow);
					forceHandleRowSelect(nRow, selected);
				})
				.onPageComplete(function (gr) {
					forceUpdate(gr);
				})
				.Create();

			return () => {
				try {
					unmountComponentsAtGrid(grid);
					grid.fnDestroy();
				}
				catch (e) {
					console.error("Error during grid.fnDestroy", e);
				}
			}
		}
		catch (e) {
			console.error("Error during init grid", e);
		}

	}, []);

	function unmountComponentsAtGrid(oGrid) {

		for (let i = 0; i < addedNodes.length; i++) {
			const cell = addedNodes[i];
			ReactDOM.unmountComponentAtNode(cell);
		}
	}

	function renderCheckboxToColumn(grid) {

		const tableBody = grid.oGrid.nTBody as HTMLElement;
		var column = tableBody.querySelectorAll("tr td:first-child:not(.dataTables_empty)");

		for (let i = 0; i < column.length; i++) {
			const cell = column[i] as HTMLElement;
			ReactDOM.render(<SearchDialogCheckbox parent={cell} />, cell);
		}

		const elements = addedNodes.concat(column as any);
		setAddedNodes(elements);
	}

	return <>
		<Filtering
			ref={_filtering}
			manageFiltersPermission={props.manageFiltersPermission}
		/>
		<Checkboxs selected={selected} setSelected={setSelected} gridRef={gridRef} />
		<div id="xgrid" ref={gridRef}>
			<table className="display resourceGrid grid-scrollable grid-scrollable-resources" style={{ height: "calc(100vh - 450px)" }} id={props.gridId}>
				<tfoot><tr></tr></tfoot>
			</table>
		</div>
	</>
}